"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import { Toast } from "./Toast.js";
const ToastContext = createContext(null);
/**
 * Generate a unique id. Prefers `crypto.randomUUID()` (available in modern
 * browsers + Node 16.7+); falls back to a non-cryptographic random string
 * for older environments. Toast ids are scoped to the in-memory queue, so
 * collision risk is negligible either way.
 */
function makeId() {
    if (typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}
/**
 * ToastProvider — global queue + bottom-right portal region. Wrap the
 * application at the root (or a sub-tree if scope is desired). Consumers
 * call `useToast().push({...})` to enqueue.
 *
 * Default duration: 4000ms. Pass `duration: 0` to disable auto-dismiss.
 */
export function ToastProvider({ children }) {
    const [items, setItems] = useState([]);
    const [mounted, setMounted] = useState(false);
    const timers = useRef({});
    const dismiss = useCallback((id) => {
        setItems((xs) => xs.filter((x) => x.id !== id));
        const t = timers.current[id];
        if (t) {
            clearTimeout(t);
            delete timers.current[id];
        }
    }, []);
    const push = useCallback((t) => {
        const id = makeId();
        const entry = { id, duration: 4000, ...t };
        setItems((xs) => [...xs, entry]);
        if ((entry.duration ?? 0) > 0) {
            timers.current[id] = setTimeout(() => dismiss(id), entry.duration);
        }
        return id;
    }, [dismiss]);
    // Cleanup any outstanding timers if the provider unmounts.
    useEffect(() => {
        setMounted(true);
        return () => {
            Object.values(timers.current).forEach((t) => clearTimeout(t));
            timers.current = {};
        };
    }, []);
    return (_jsxs(ToastContext.Provider, { value: { push, dismiss }, children: [children, mounted
                ? createPortal(_jsx("div", { "data-slot": "toast-region", "aria-live": "polite", "aria-relevant": "additions text", className: "fixed bottom-6 end-6 z-50 flex flex-col gap-2", children: items.map((t) => (_jsx(Toast, { ...t, onDismiss: () => dismiss(t.id) }, t.id))) }), document.body)
                : null] }));
}
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error("useToast must be used inside <ToastProvider>");
    return ctx;
}
